import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDown,
  ArrowRight,
  MoveUpRight,
  Pause,
  Play,
} from "lucide-react";
import ScrollFilm from "./ScrollFilm";
import WorkCard from "../studio/WorkCard";
import { clients } from "../../data/clients";
import { afterLoadIdle, isConstrainedDevice } from "../../utils/loadBudget";
import styles from "./StudioHome.module.css";

const R01Scene = lazy(() => import("./R01Scene"));
const servicePaths = [
  ["/services/ai-implementation", "/services/ai-agents"],
  ["/services", "/services/creative-engine"],
  ["/services/shopify", "/services/saas"],
  ["/services/seo", "/services/geo", "/services/cro"],
];

export default function StudioHome() {
  const { t, i18n } = useTranslation("studio");
  const lang = i18n.language?.startsWith("es") ? "/es" : "";
  const heroRef = useRef(null),
    filmRef = useRef(null),
    workRef = useRef(null);
  const [workCount, setWorkCount] = useState(4);
  const systemReduced = useReducedMotion();
  /* With Save-Data, on real 2G or on a 1 GB device the page starts in its
     still state: posters instead of the WebGL model and the frame films.
     The motion toggle below lets the visitor opt back in. */
  const [paused, setPaused] = useState(isConstrainedDevice);
  const reduced = Boolean(systemReduced || paused);
  /* The scene is decoration over a poster that is already the LCP image,
     so it waits until the page has finished loading and the main thread
     is idle — its download and its ~0.8 s of setup on a mid-range phone
     used to land inside the first paint. */
  const [sceneArmed, setSceneArmed] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  useEffect(() => afterLoadIdle(() => setSceneArmed(true)), []);
  const showScene = sceneArmed && !reduced;
  const facts = t("intro.facts", { returnObjects: true });
  const services = t("services.rows", { returnObjects: true });
  return (
    <div className={styles.page}>
      <section
        ref={heroRef}
        className={`${styles.heroRange} ${reduced ? styles.stillRange : ""}`}
        aria-labelledby="studio-title"
      >
        <div className={styles.hero}>
          <div className={styles.heroCopy}>
            <h1 id="studio-title" className={styles.title}>
              <span>{t("hero.line1")}</span>
              <span>{t("hero.line2")}</span>
              <span className={styles.lastLine}>
                {t("hero.line3")}
                <span className={styles.square} aria-hidden="true" />
              </span>
            </h1>
            <p className={styles.heroPositioning}>{t("hero.positioning")}</p>
            <p className={styles.heroDescription}>{t("hero.description")}</p>
            <div className={styles.heroActions}>
              <Link className={styles.button} to={`${lang}/quote`}>
                {t("hero.cta")}
                <ArrowUpRight size={21} strokeWidth={1.75} />
              </Link>
              <a className={styles.textLink} href="#selected-work">
                {t("hero.work")}
                <ArrowDown size={16} />
              </a>
            </div>
          </div>
          <div className={styles.modelStage}>
            <div className={styles.modelNumber} aria-hidden="true">
              R-01
            </div>
            <div className={styles.modelRing} aria-hidden="true" />
            <img
              className={`${styles.modelFallback} ${showScene && sceneReady ? styles.modelFallbackHidden : ""}`}
              src="/images/studio/r01-poster.webp"
              width="1400"
              height="1400"
              alt={t("hero.modelAlt")}
              fetchpriority="high"
            />
            {showScene && (
              <Suspense fallback={null}>
                <R01Scene
                  rangeRef={heroRef}
                  reducedMotion={reduced}
                  onReady={setSceneReady}
                />
              </Suspense>
            )}
            <div className={styles.modelCaption}>
              <span>{t("hero.model")}</span>
              <span>26 / 26</span>
            </div>
          </div>
          <div className={styles.heroFoot}>
            <span>{t("hero.note")}</span>
            {!reduced && (
              <span className={styles.scrollHint}>
                <ArrowDown size={14} />
                {t("hero.scroll")}
              </span>
            )}
            <button
              className={styles.motionToggle}
              aria-label={t("hero.motionLabel")}
              aria-pressed={!reduced}
              disabled={Boolean(systemReduced)}
              onClick={() => setPaused((v) => !v)}
            >
              {reduced ? <Play size={13} /> : <Pause size={13} />}
              {t(reduced ? "hero.motionOff" : "hero.motionOn")}
            </button>
          </div>
        </div>
      </section>

      <section className={styles.intro} aria-labelledby="studio-intro">
        <h2 id="studio-intro">
          {t("intro.title")}
          <br />
          <span>{t("intro.accent")}</span>
        </h2>
        <div className={styles.introAside}>
          <p>{t("intro.body")}</p>
          <dl className={styles.facts}>
            {facts.map((fact) => (
              <div key={fact.value}>
                <dt>{fact.value}</dt>
                <dd>{fact.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section
        id="selected-work"
        ref={workRef}
        className={styles.work}
        aria-labelledby="work-title"
      >
        <div className={styles.sectionHead}>
          <div>
            <h2 id="work-title">{t("work.title")}</h2>
            <p>{t("work.description")}</p>
          </div>
          <Link to={`${lang}/projects`} className={styles.textLink}>
            {t("workIndex.allProjects")}
            <ArrowUpRight size={19} />
          </Link>
        </div>
        <div id="home-work-grid" className={styles.workGrid}>
          {clients.slice(0, workCount).map((client, index) => (
            <WorkCard key={client.slug} item={client} index={index} />
          ))}
        </div>
        <div className={styles.workActions}>
          <p role="status">{t("workIndex.homeCount", { count: workCount })}</p>
          {workCount < 8 ? (
            <button
              className={styles.loadMore}
              type="button"
              aria-controls="home-work-grid"
              onClick={() => {
                setWorkCount(8);
                requestAnimationFrame(() =>
                  workRef.current
                    ?.querySelectorAll("[data-work-card] a")[4]
                    ?.focus({ preventScroll: true }),
                );
              }}
            >
              {t("workIndex.loadFour")}
              <ArrowDown size={19} />
            </button>
          ) : (
            <Link to={`${lang}/projects`} className={styles.loadMore}>
              {t("workIndex.keepExploring")}
              <ArrowUpRight size={20} />
            </Link>
          )}
        </div>
        <Link to={`${lang}/projects?type=products`} className={styles.labLink}>
          <span>
            {t("work.ownTitle")}
            <small>{t("work.ownBody")}</small>
          </span>
          <span>
            {t("work.ownLink")}
            <ArrowUpRight size={24} />
          </span>
        </Link>
      </section>

      <section
        ref={filmRef}
        className={`${styles.filmRange} ${reduced ? styles.stillRange : ""}`}
        aria-labelledby="film-title"
      >
        <div className={styles.filmSticky}>
          <div className={styles.filmTop}>
            <span>{t("film.label")}</span>
            <span>{!reduced && t("film.scroll")}</span>
          </div>
          <div className={styles.filmMedia}>
            <ScrollFilm
              basePath="/films/r01-assembly"
              frameCount={72}
              poster="/images/studio/r01-film.webp"
              alt={t("film.alt")}
              rangeRef={filmRef}
              reducedMotion={reduced}
            />
          </div>
          <div className={styles.filmCopy}>
            <h2 id="film-title">
              {t("film.title")}
              <br />
              <span>{t("film.accent")}</span>
            </h2>
            <p>{t("film.description")}</p>
          </div>
          <div className={styles.filmBottom}>
            <span>01 / {t("film.start")}</span>
            <span>
              02 / {t("film.end")}
              <MoveUpRight size={18} />
            </span>
          </div>
        </div>
      </section>

      <section className={styles.services} aria-labelledby="services-title">
        <div className={styles.sectionHead}>
          <div>
            <h2 id="services-title">{t("services.title")}</h2>
            <p>{t("services.description")}</p>
          </div>
          <ArrowDown size={42} strokeWidth={1} aria-hidden="true" />
        </div>
        {services.map((service, index) => (
          <article className={styles.serviceRow} key={service.title}>
            <span className={styles.serviceNumber}>0{index + 1}</span>
            <h3>{service.title}</h3>
            <div>
              <p>{service.description}</p>
              <div className={styles.serviceLinks}>
                {service.links.map((label, i) => (
                  <Link to={`${lang}${servicePaths[index][i]}`} key={label}>
                    {label}
                    <ArrowUpRight size={14} />
                  </Link>
                ))}
              </div>
            </div>
          </article>
        ))}
        <div className={styles.detail}>
          <ScrollFilm
            basePath="/films/r01-optics"
            frameCount={48}
            poster="/images/studio/r01-optics.webp"
            alt={t("services.detailAlt")}
            reducedMotion={reduced}
            cover
          />
          <p>{t("services.detailCaption")}</p>
          <span aria-hidden="true">
            R-01
            <br />
            OPTICS
          </span>
        </div>
      </section>

      <section className={styles.closing} aria-labelledby="closing-title">
        <div>
          <h2 id="closing-title">
            {t("closing.title")}
            <br />
            <span>{t("closing.accent")}</span>
          </h2>
          <p>{t("closing.body")}</p>
        </div>
        <div className={styles.closingAction}>
          <Link
            to={`${lang}/quote`}
            className={styles.roundCta}
            aria-label={t("closing.cta")}
          >
            <ArrowUpRight strokeWidth={1} />
          </Link>
          <Link to={`${lang}/quote`} className={styles.closingText}>
            {t("closing.cta")}
            <ArrowRight size={18} />
          </Link>
        </div>
        <p className={styles.closingSmall}>{t("closing.small")}</p>
      </section>
    </div>
  );
}
